// src/features/auth/context/AuthContext.jsx
// Enterprise-grade auth context with proper Google OAuth redirect handling

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { onAuthStateChanged, reload } from 'firebase/auth';
import * as authService from '../services/authService';
import { clearPendingEntries } from '../../journal/services/journalService';
import { auth } from '@/shared/utils/core/firebase-config';
import * as biometricService from '../services/biometricService';
import * as profileService from '../../profile/services/profileService';
import analytics from '@/shared/utils/analytics/analytics';
import { identifyUser as identifyPostHogUser, resetPostHogUser } from '@/shared/utils/analytics/posthog';
import logger from '@/shared/utils/core/logger';

const AuthContext = createContext({
  user: null,
  loading: true,
  error: null,
  authInitialized: false,
  biometricAvailable: false,
  biometricRegistered: false,
  googleAuthPending: false,
  loginWithGoogle: async () => {},
  loginWithEmail: async () => {},
  signupWithEmail: async () => {},
  resetPassword: async () => {},
  logout: async () => {},
  registerBiometric: async () => {},
  loginWithBiometric: async () => {},
  removeBiometric: async () => {},
  resendEmailVerification: async () => {},
  refreshUser: async () => {},
  clearError: () => {}
});

export function AuthProvider({ children }) {
  // Core auth state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Biometric state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricRegistered, setBiometricRegistered] = useState(false);

  // Google auth state
  const [googleAuthPending, setGoogleAuthPending] = useState(
    authService.isGoogleAuthPending()
  );

  /**
   * Initialize new user profile after first Google sign-in
   * Creates initial profile document with onboarding flags
   */
  const initializeNewUser = useCallback(async (firebaseUser) => {
    const LOG_PREFIX = '[AuthContext] New User';

    try {
      logger.debug(`${LOG_PREFIX} Initializing new user:`, firebaseUser.email);

      // Create initial profile document
      await profileService.createInitialProfile(firebaseUser.uid, {
        displayName: firebaseUser.displayName || '',
        email: firebaseUser.email || '',
        photoURL: firebaseUser.photoURL || '',
        provider: 'google',
        emailVerified: firebaseUser.emailVerified || false
      });

      // Track new user signup in analytics
      analytics.trackEvent('user_signup', {
        method: 'google',
        uid: firebaseUser.uid,
        email: firebaseUser.email
      });

      logger.debug(`${LOG_PREFIX} New user initialized successfully`);
    } catch (error) {
      logger.error(`${LOG_PREFIX} Failed to initialize new user:`, error);
      // Don't throw - allow auth to complete even if profile creation fails
      // Profile can be created later during onboarding
    }
  }, []);

  // Handle Google OAuth redirect result on page load
  useEffect(() => {
    let isMounted = true;
    let unsubscribeAuth = null;
    const LOG_PREFIX = '[AuthContext]';

    const initializeAuth = async () => {
      if (import.meta.env.DEV) {
        logger.debug(`${LOG_PREFIX} Initializing authentication...`);
      }

      // STEP 1: Set up Firebase auth state listener FIRST
      // This ensures we capture the user if they're already logged in
      const auth = authService.getAuthInstance();

      // Create a promise that resolves when we get the first auth state
      let resolveInitialAuth;
      const initialAuthPromise = new Promise(resolve => {
        resolveInitialAuth = resolve;
      });

      let isFirstAuthState = true;
      unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
        if (!isMounted) return;

        setUser(firebaseUser);

        if (firebaseUser) {
          // Start session tracking
          analytics.startSession();
          analytics.setUser(firebaseUser.uid);

          // Identify user in PostHog for user profiles and session recording
          identifyPostHogUser(firebaseUser);

          // Sync mutable auth fields (emailVerified, displayName, photoURL, timezone,
          // lastActiveAt) into userProfiles — throttled to once per browser session
          // Guarded: WKWebView with ITP can throw on sessionStorage access
          const sessionKey = 'clarity_profile_synced';
          try {
            if (!sessionStorage.getItem(sessionKey)) {
              sessionStorage.setItem(sessionKey, '1');
              profileService.syncProfileFromAuth(firebaseUser.uid, firebaseUser).catch(() => {});
            }
          } catch { /* sessionStorage unavailable (WKWebView ITP) — sync on every auth state */ }
        } else {
          // Reset PostHog user on logout
          resetPostHogUser();
          try { sessionStorage.removeItem('clarity_profile_synced'); } catch { /* ignore */ }
        }

        // Resolve the initial auth promise on first state
        if (isFirstAuthState) {
          isFirstAuthState = false;
          resolveInitialAuth(firebaseUser);
        }
      });

      try {
        // STEP 2: Check for Google OAuth redirect result
        // This MUST be called on every page load to handle OAuth redirects
        if (import.meta.env.DEV) {
          logger.debug(`${LOG_PREFIX} Checking for Google redirect result...`);
        }

        const googleResult = await authService.initializeGoogleAuth();

        if (googleResult.error) {
          if (import.meta.env.DEV) {
            logger.error(`${LOG_PREFIX} Google auth init error:`, googleResult.error);
          }
          // Don't set error state for normal "no redirect result" cases
          if (googleResult.error.code && googleResult.error.code !== 'auth/no-redirect-result') {
            setError(googleResult.error.message);
          }
        } else if (googleResult.user) {
          logger.debug(`${LOG_PREFIX} User authenticated via Google redirect:`, googleResult.user.email);
          logger.debug(`${LOG_PREFIX} Waiting for onAuthStateChanged to fire...`);

          // ✅ CRITICAL: Wait for onAuthStateChanged to set the user state
          // This prevents race condition where we mark auth as initialized
          // before the user state is actually set
          await initialAuthPromise;
          logger.debug(`${LOG_PREFIX} onAuthStateChanged fired, user state set`);

          // ✅ NEW: Initialize profile for new users AFTER user state is set
          if (googleResult.isNewUser) {
            logger.debug(`${LOG_PREFIX} Detected new user, initializing profile...`);
            await initializeNewUser(googleResult.user);
            logger.debug(`${LOG_PREFIX} New user profile created`);
          }
        } else {
          // No redirect result - normal page load
          // Wait for initial auth state check
          await initialAuthPromise;
        }

        if (import.meta.env.DEV) {
          logger.debug(`${LOG_PREFIX} Auth initialization complete`);
        }

      } catch (err) {
        logger.error(`${LOG_PREFIX} Error during initialization:`, err);
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setAuthInitialized(true);
          setGoogleAuthPending(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
      analytics.endSession();
    };
  }, []);

  useEffect(() => {
    const checkBiometricSupport = async () => {
      try {
        const support = await biometricService.checkBiometricSupport();
        setBiometricAvailable(support.supported);
      } catch (err) {
        logger.warn('Biometric check failed:', err);
        setBiometricAvailable(false);
      }
    };

    checkBiometricSupport();
  }, []);

  // Check biometric registration when user changes
  useEffect(() => {
    const checkBiometricRegistration = async () => {
      if (user) {
        try {
          const hasRegistered = await biometricService.hasBiometricRegistered(user.uid);
          setBiometricRegistered(hasRegistered);
        } catch (err) {
          logger.warn('Biometric registration check failed:', err);
          setBiometricRegistered(false);
        }
      } else {
        setBiometricRegistered(false);
      }
    };

    checkBiometricRegistration();
  }, [user?.uid]); // uid-only dep — avoid re-checking on token refresh (new user object, same uid)

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loginWithGoogle = useCallback(async (rememberMe = true) => {
    try {
      setError(null);
      setGoogleAuthPending(true);

      const result = await authService.loginWithGoogle(rememberMe);

      // If result is null, redirect is in progress
      if (result === null) {
        // Keep googleAuthPending true - page will redirect
        return null;
      }

      setGoogleAuthPending(false);
      return result;

    } catch (err) {
      setGoogleAuthPending(false);
      setError(err.message);
      throw err;
    }
  }, []);

  const loginWithEmail = useCallback(async (email, password, rememberMe = true) => {
    try {
      setError(null);
      const result = await authService.loginWithEmail(email, password, rememberMe);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const signupWithEmail = useCallback(async (email, password, displayName) => {
    try {
      setError(null);
      const result = await authService.signupWithEmail(email, password, displayName);
      // Create Firestore profile for new email users (Google OAuth handles this separately)
      if (result?.user) {
        await profileService.createInitialProfile(result.user.uid, {
          displayName: displayName || result.user.displayName || '',
          email: result.user.email || '',
          photoURL: result.user.photoURL || '',
          provider: 'email',
          emailVerified: result.user.emailVerified || false
        }).catch(err => logger.warn('[AuthContext] Email signup profile creation failed:', err));
      }
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    try {
      setError(null);
      await authService.resetPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setError(null);
      const userId = auth.currentUser?.uid;
      // Clear offline queue and session sync flag before signing out
      await clearPendingEntries();
      sessionStorage.removeItem('clarity_profile_synced');
      await authService.logout(userId);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const resendEmailVerification = useCallback(async () => {
    try {
      setError(null);
      await authService.resendEmailVerification();
      logger.debug('[AuthContext] Verification email resent');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      setError(null);
      const currentUser = auth.currentUser;
      if (currentUser) {
        await reload(currentUser);
        // Update local state with refreshed user data
        setUser({ ...currentUser });
        logger.debug('[AuthContext] User refreshed, emailVerified:', currentUser.emailVerified);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const registerBiometric = useCallback(async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Must be logged in to register biometric authentication');
      }

      const result = await biometricService.registerBiometric(
        currentUser.uid,
        currentUser.email,
        currentUser.displayName
      );

      if (result.success) {
        setBiometricRegistered(true);
        analytics.trackUserPreferences({ biometricEnabled: true });
      }

      return result;
    } catch (err) {
      logger.error('Biometric registration error:', err);
      throw err;
    }
  }, []);

  const loginWithBiometric = useCallback(async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Must have an account to use biometric login');
      }

      const result = await biometricService.authenticateWithBiometric(currentUser.uid);
      return result;
    } catch (err) {
      logger.error('Biometric login error:', err);
      throw err;
    }
  }, []);

  const removeBiometric = useCallback(async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Must be logged in to remove biometric authentication');
      }

      const result = await biometricService.removeBiometric(currentUser.uid);

      if (result.success) {
        setBiometricRegistered(false);
        analytics.trackUserPreferences({ biometricEnabled: false });
      }

      return result;
    } catch (err) {
      logger.error('Remove biometric error:', err);
      throw err;
    }
  }, []);

  const contextValue = useMemo(() => ({
    user,
    loading,
    error,
    authInitialized,
    biometricAvailable,
    biometricRegistered,
    googleAuthPending,
    loginWithGoogle,
    loginWithEmail,
    signupWithEmail,
    resetPassword,
    logout,
    registerBiometric,
    loginWithBiometric,
    removeBiometric,
    resendEmailVerification,
    refreshUser,
    clearError,
  }), [
    user, loading, error, authInitialized,
    biometricAvailable, biometricRegistered, googleAuthPending,
    loginWithGoogle, loginWithEmail, signupWithEmail, resetPassword,
    logout, registerBiometric, loginWithBiometric, removeBiometric,
    resendEmailVerification, refreshUser, clearError,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
